import React from "react";
import { Info, AlertTriangle } from "lucide-react";
import PassportOcrCard from "@/components/PassportOcrCard";
import {
  ALL_COUNTRIES, CIVIL_STATUSES, SEX_OPTIONS, PURPOSE_OPTIONS, ENTRIES_OPTIONS,
  RELATIONSHIP_OPTIONS, EU_RELATIONSHIP_OPTIONS, TRAVEL_DOC_TYPES, OCCUPATIONS,
  COST_COVERED_OPTIONS, MEANS_OF_SUPPORT_OPTIONS, isMinor, totalDays,
} from "@/lib/applicants";
import { SCHENGEN_COUNTRIES } from "@/lib/countries";
import PhoneInput from "@/components/PhoneInput";
import DateInput from "@/components/DateInput";
import { CountryCombobox } from "@/components/CountryCombobox";
import { Flag } from "@/components/Flag";

const SCHENGEN_NAMES = SCHENGEN_COUNTRIES.map((c) => c.name);

export default function ApplicantForm({ value, onChange, step, showRelationship, applicantIndex = 0, destinationCountry = "" }) {
  const a = value;
  const set = (patch) => onChange({ ...a, ...patch });
  const idp = `applicant-${applicantIndex}`;
  // Auto-derive minor flag from DOB if DOB is set, otherwise fall back to manual selection
  const dobMinor = a.date_of_birth ? isMinor(a.date_of_birth) : null;
  const minorSelected = dobMinor !== null ? dobMinor : (a.minor_applicant === "yes");

  return (
    <div className="space-y-6">
      {step === "passport" && <PassportStep a={a} set={set} idp={idp} applicantIndex={applicantIndex} minorSelected={minorSelected} dobMinor={dobMinor} />}
      {step === "application" && <ApplicationStep a={a} set={set} idp={idp} />}
      {step === "travel" && <TravelStep a={a} set={set} idp={idp} showRelationship={showRelationship} destinationCountry={destinationCountry} />}
      {step === "additional" && <AdditionalStep a={a} set={set} idp={idp} />}
      <style>{`.inp { width: 100%; border: 1px solid #e2e8f0; background: #fff; color: #0f1b2d; border-radius: 6px; padding: 10px 12px; outline: none; transition: all 0.15s; font-size: 14px; }
        .inp:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
        .inp:disabled { background: #f1f5f9; color: #475569; cursor: not-allowed; }`}</style>
    </div>
  );
}

function PassportStep({ a, set, idp, applicantIndex, minorSelected, dobMinor }) {
  const mismatch = a.passport_number && a.passport_number_confirm && a.passport_number !== a.passport_number_confirm;
  const [highlighted, setHighlighted] = React.useState(() => new Set());
  const [expiryNotice, setExpiryNotice] = React.useState(null);

  // Wrap `set` so ANY field the user edits loses its OCR highlight.
  const setAndUnhighlight = React.useCallback((patch) => {
    set(patch);
    setHighlighted((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      let changed = false;
      for (const k of Object.keys(patch)) {
        if (next.delete(k)) changed = true;
      }
      return changed ? next : prev;
    });
  }, [set]);

  const handleOcrExtracted = React.useCallback(({ patch, filledKeys, expiryNotice: notice }) => {
    set(patch); // apply raw patch without unhighlighting the just-filled fields
    setHighlighted(new Set(filledKeys));
    setExpiryNotice(notice || null);
    // Scroll the form into view so users see the filled fields.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${idp}-surname"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [set, idp]);

  const hl = (k) => highlighted.has(k);

  // When DOB is set, auto-sync minor_applicant. Use effect-like pattern with a controlled value.
  React.useEffect(() => {
    if (dobMinor !== null) {
      const desired = dobMinor ? "yes" : "no";
      if (a.minor_applicant !== desired) set({ minor_applicant: desired });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dobMinor]);

  return (
    <>
      <PassportOcrCard applicantIndex={applicantIndex} onExtracted={handleOcrExtracted} />

      {expiryNotice && (
        <div
          className={`mb-4 rounded-lg border p-3 flex items-start gap-3 text-sm ${
            expiryNotice.level === "error" ? "border-red-200 bg-red-50 text-red-900" :
            expiryNotice.level === "warning" ? "border-amber-200 bg-amber-50 text-amber-900" :
            "border-blue-200 bg-blue-50 text-blue-900"
          }`}
          data-testid={`${idp}-passport-expiry-notice`}
        >
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div className="flex-1">{expiryNotice.message}</div>
        </div>
      )}

      <Section title="Personal Information">
        <Row2>
          <Field label="Surname (Family name)" required hl={hl("surname")}><TextIn v={a.surname} on={(v) => setAndUnhighlight({ surname: v })} t={`${idp}-surname`} /></Field>
          <Field label="Surname at birth" required hl={hl("surname_at_birth")}><TextIn v={a.surname_at_birth} on={(v) => setAndUnhighlight({ surname_at_birth: v })} t={`${idp}-surname-birth`} /></Field>
        </Row2>
        <Field label="First name(s)" required hl={hl("first_names")}><TextIn v={a.first_names} on={(v) => setAndUnhighlight({ first_names: v })} t={`${idp}-first-names`} /></Field>
        <Row2>
          <Field label="Date of birth" required hl={hl("date_of_birth")}><DateIn v={a.date_of_birth} on={(v) => setAndUnhighlight({ date_of_birth: v })} t={`${idp}-dob`} /></Field>
          <Field label="Place of birth (city)" required hl={hl("place_of_birth")}><TextIn v={a.place_of_birth} on={(v) => setAndUnhighlight({ place_of_birth: v })} t={`${idp}-pob`} /></Field>
        </Row2>
        <Row2>
          <Field label="Country of birth" required hl={hl("country_of_birth")}><CountrySelect v={a.country_of_birth} on={(v) => setAndUnhighlight({ country_of_birth: v })} t={`${idp}-cob`} /></Field>
          <Field label="Current nationality" required hl={hl("nationality")}><CountrySelect v={a.nationality} on={(v) => setAndUnhighlight({ nationality: v })} t={`${idp}-nationality`} /></Field>
        </Row2>
        <Row2>
          <Field label="Nationality at birth" required><CountrySelect v={a.nationality_at_birth} on={(v) => setAndUnhighlight({ nationality_at_birth: v })} t={`${idp}-nationality-birth`} /></Field>
          <Field label="Nationality — Others" hint="Optional"><CountrySelect optional v={a.nationality_other} on={(v) => setAndUnhighlight({ nationality_other: v })} t={`${idp}-nationality-other`} /></Field>
        </Row2>
        <Row2>
          <Field label="Sex" required hl={hl("sex")}>
            <SelectIn v={a.sex} on={(v) => setAndUnhighlight({ sex: v })} options={SEX_OPTIONS} t={`${idp}-sex`} />
          </Field>
          <Field label="Marital status" required>
            <SelectIn v={a.civil_status} on={(v) => setAndUnhighlight({ civil_status: v })} options={CIVIL_STATUSES} t={`${idp}-civil`} />
          </Field>
        </Row2>
        <Row2>
          <Field label="Minor applicant (under 18)" required hint={dobMinor !== null ? "Auto-calculated from date of birth" : "Please confirm"}>
            <YesNo
              v={a.minor_applicant}
              on={(v) => setAndUnhighlight({ minor_applicant: v })}
              t={`${idp}-minor`}
              disabled={dobMinor !== null}
            />
          </Field>
          <Field label="Applicant's ID number" hint="Optional"><TextIn optional v={a.applicant_id_number} on={(v) => setAndUnhighlight({ applicant_id_number: v })} t={`${idp}-id`} /></Field>
        </Row2>
      </Section>

      <Section title="Passport / Travel Document">
        <Field label="Type of passport" required>
          <SelectIn v={a.travel_document_type} on={(v) => setAndUnhighlight({ travel_document_type: v })} options={TRAVEL_DOC_TYPES} t={`${idp}-doc-type`} />
        </Field>
        <Row2>
          <Field label="Passport number" required hl={hl("passport_number")}><TextIn v={a.passport_number} on={(v) => setAndUnhighlight({ passport_number: v.toUpperCase() })} t={`${idp}-passport`} extra="uppercase" /></Field>
          <Field label="Re-enter passport number" required hl={hl("passport_number_confirm")}>
            <TextIn v={a.passport_number_confirm} on={(v) => setAndUnhighlight({ passport_number_confirm: v.toUpperCase() })} t={`${idp}-passport-confirm`} extra="uppercase" />
            {mismatch && <div className="text-xs text-red-600 mt-1">Passport numbers don&apos;t match</div>}
          </Field>
        </Row2>
        <Row2>
          <Field label="Passport date of issue" required hl={hl("passport_issue_date")}><DateIn v={a.passport_issue_date} on={(v) => setAndUnhighlight({ passport_issue_date: v })} t={`${idp}-passport-issue`} /></Field>
          <Field label="Passport valid until" required hl={hl("passport_expiry_date")}><DateIn v={a.passport_expiry_date} on={(v) => setAndUnhighlight({ passport_expiry_date: v })} t={`${idp}-passport-expiry`} /></Field>
        </Row2>
        <Row2>
          <Field label="Issued country" required hl={hl("passport_issued_country")}><CountrySelect v={a.passport_issued_country} on={(v) => setAndUnhighlight({ passport_issued_country: v })} t={`${idp}-passport-country`} /></Field>
          <Field label="Issued by (authority)" required hl={hl("passport_issued_by_authority")}><TextIn v={a.passport_issued_by_authority} on={(v) => setAndUnhighlight({ passport_issued_by_authority: v })} t={`${idp}-passport-authority`} /></Field>
        </Row2>
      </Section>

      {minorSelected && (
        <Section title="Parent / Guardian (Minor Applicant)" subtitle="Required because applicant is a minor.">
          <Field label="Parent/Guardian full name" required><TextIn v={a.guardian_name} on={(v) => set({ guardian_name: v })} t={`${idp}-guardian-name`} /></Field>
          <Field label="Parent/Guardian address" required>
            <textarea required rows={2} value={a.guardian_address} onChange={(e) => set({ guardian_address: e.target.value })} className="inp" data-testid={`${idp}-guardian-address`} />
          </Field>
          <Row2>
            <Field label="Parent/Guardian phone" required>
              <PhoneIn v={a.guardian_phone} on={(v) => set({ guardian_phone: v })} t={`${idp}-guardian-phone`} />
            </Field>
            <Field label="Parent/Guardian email" required><TextIn v={a.guardian_email} on={(v) => set({ guardian_email: v })} t={`${idp}-guardian-email`} type="email" /></Field>
          </Row2>
          <Field label="Parent/Guardian nationality" required><CountrySelect v={a.guardian_nationality} on={(v) => set({ guardian_nationality: v })} t={`${idp}-guardian-nationality`} /></Field>
        </Section>
      )}
    </>
  );
}

function ApplicationStep({ a, set, idp }) {
  const showOccupationOther = a.occupation === "Other";
  const hideEmployer = a.occupation === "No occupation";
  return (
    <>
      <Section title="Address & Contact">
        <Field label="Home address" required>
          <textarea required rows={2} value={a.home_address} onChange={(e) => set({ home_address: e.target.value })} className="inp" data-testid={`${idp}-address`} />
        </Field>
        <Row2>
          <Field label="Street" required><TextIn v={a.application_street} on={(v) => set({ application_street: v })} t={`${idp}-street`} /></Field>
          <Field label="City" required><TextIn v={a.application_city} on={(v) => set({ application_city: v })} t={`${idp}-city`} /></Field>
        </Row2>
        <Row2>
          <Field label="Country" required><CountrySelect v={a.applicant_country} on={(v) => set({ applicant_country: v })} t={`${idp}-country`} /></Field>
          <Field label="Postal code" required><TextIn v={a.applicant_postal_code} on={(v) => set({ applicant_postal_code: v })} t={`${idp}-postal`} /></Field>
        </Row2>
        <Row2>
          <Field label="Email" required><TextIn v={a.email} on={(v) => set({ email: v })} t={`${idp}-email`} type="email" /></Field>
          <Field label="Telephone" required>
            <PhoneIn v={a.phone} on={(v) => set({ phone: v })} t={`${idp}-phone`} />
          </Field>
        </Row2>
        <Field label="Current occupation" required>
          <SelectIn v={a.occupation} on={(v) => set({ occupation: v })} options={OCCUPATIONS} t={`${idp}-occupation`} />
        </Field>
        {showOccupationOther && (
          <Field label="Please specify your occupation" required>
            <TextIn v={a.occupation_other} on={(v) => set({ occupation_other: v })} t={`${idp}-occupation-other`} />
          </Field>
        )}
      </Section>

      <Section title="Residence">
        <Field label="Is your residence in a country other than country of current nationality?" required>
          <YesNo v={a.residence_different} on={(v) => set({ residence_different: v })} t={`${idp}-residence-different`} />
        </Field>
        {a.residence_different === "yes" && (
          <>
            <Field label="Country of residence" required>
              <CountrySelect v={a.residence_country} on={(v) => set({ residence_country: v })} t={`${idp}-residence-country`} />
            </Field>
            <Row2>
              <Field label="Residence permit number" required><TextIn v={a.residence_permit_no} on={(v) => set({ residence_permit_no: v })} t={`${idp}-permit-no`} /></Field>
              <Field label="Residence permit valid until" required><DateIn v={a.residence_permit_valid_until} on={(v) => set({ residence_permit_valid_until: v })} t={`${idp}-permit-until`} /></Field>
            </Row2>
          </>
        )}
      </Section>

      {!hideEmployer && (
        <Section title="Employer / Education establishment">
          <Field label="Name of employer or education establishment" required><TextIn v={a.employer_name} on={(v) => set({ employer_name: v })} t={`${idp}-employer-name`} /></Field>
          <Field label="Address" required>
            <textarea required rows={2} value={a.employer_address} onChange={(e) => set({ employer_address: e.target.value })} className="inp" data-testid={`${idp}-employer-address`} />
          </Field>
          <Row2>
            <Field label="Telephone" required>
              <PhoneIn v={a.employer_phone} on={(v) => set({ employer_phone: v })} t={`${idp}-employer-phone`} />
            </Field>
            <Field label="City" required><TextIn v={a.employer_city} on={(v) => set({ employer_city: v })} t={`${idp}-employer-city`} /></Field>
          </Row2>
          <Row2>
            <Field label="Postal code" required><TextIn v={a.employer_postal_code} on={(v) => set({ employer_postal_code: v })} t={`${idp}-employer-postal`} /></Field>
            <Field label="Country" required><CountrySelect v={a.employer_country} on={(v) => set({ employer_country: v })} t={`${idp}-employer-country`} /></Field>
          </Row2>
        </Section>
      )}

      <Section title="Fingerprints">
        <Field label="Have your fingerprints been collected previously for a Schengen visa?" required>
          <YesNo v={a.fingerprints_collected} on={(v) => set({ fingerprints_collected: v })} t={`${idp}-fp`} />
        </Field>
        {a.fingerprints_collected === "yes" && (
          <>
            <Field label="Date fingerprints were collected" required><DateIn v={a.fingerprints_date} on={(v) => set({ fingerprints_date: v })} t={`${idp}-fp-date`} /></Field>
            <Row2>
              <Field label="Schengen country that issued the previous visa" required>
                <SchengenSelect v={a.fingerprints_sticker_country} on={(v) => set({ fingerprints_sticker_country: v })} t={`${idp}-fp-country`} />
              </Field>
              <Field label="Visa sticker number" required>
                <TextIn v={a.fingerprints_sticker_number} on={(v) => set({ fingerprints_sticker_number: v })} t={`${idp}-fp-sticker`} />
              </Field>
            </Row2>
          </>
        )}
      </Section>
    </>
  );
}

function TravelStep({ a, set, idp, showRelationship, destinationCountry = "" }) {
  const selectedDests = a.destination_countries || [];
  const days = totalDays(a.intended_arrival_date, a.intended_departure_date);
  const dateMismatch = a.intended_arrival_date && a.intended_departure_date && days === 0;
  return (
    <Section title="Travel Details">
      <Field label="Purpose of travel" required>
        <SelectIn v={a.purpose} on={(v) => set({ purpose: v })} options={PURPOSE_OPTIONS} t={`${idp}-purpose`} />
      </Field>
      <Field label="Additional information on purpose of stay" required>
        <textarea required rows={2} value={a.purpose_additional_info} onChange={(e) => set({ purpose_additional_info: e.target.value })} className="inp" data-testid={`${idp}-purpose-info`} />
      </Field>

      <Field label="Schengen State of first entry" required>
        <SchengenSelect v={a.first_entry_country} on={(v) => set({ first_entry_country: v })} t={`${idp}-first-entry`} />
      </Field>

      <Field label="Country(-ies) of destination" required hint="Select one or more">
        <MultiCountryChecks
          options={SCHENGEN_NAMES}
          value={selectedDests}
          onChange={(v) => {
            // Sync destination_nights with selected destination_countries
            const prev = Array.isArray(a.destination_nights) ? a.destination_nights : [];
            const map = Object.fromEntries(prev.map((n) => [n.country, n]));
            const nights = v.map((name) => {
              const sc = SCHENGEN_COUNTRIES.find((c) => c.name === name);
              return map[name] || { country: name, code: sc?.code || "", nights: "", mode: "arrange" };
            });
            set({ destination_countries: v, destination_nights: nights });
          }}
          testIdPrefix={`${idp}-dest`}
        />
        {selectedDests.length > 0 && (
          <div className="mt-3 px-3 py-2 rounded-md bg-[#eff6ff] border border-[#bfdbfe] text-sm text-[#0f1b2d]" data-testid={`${idp}-dest-summary`}>
            <span className="font-semibold text-[#2563eb]">Selected:</span> {selectedDests.join(", ")} <span className="text-[#475569]">({selectedDests.length} {selectedDests.length === 1 ? "country" : "countries"})</span>
          </div>
        )}
        {selectedDests.length > 1 && <NightsPerCountry a={a} set={set} idp={idp} destinationCountry={destinationCountry} />}
      </Field>

      <Row2>
        <Field label="Intended arrival date in Schengen" required>
          <DateIn v={a.intended_arrival_date} on={(v) => set({ intended_arrival_date: v })} t={`${idp}-arrival`} />
        </Field>
        <Field label="Intended departure date from Schengen" required>
          <DateIn v={a.intended_departure_date} on={(v) => set({ intended_departure_date: v })} t={`${idp}-departure`} />
        </Field>
      </Row2>
      <div data-testid={`${idp}-days-summary`} className="-mt-1 text-sm font-semibold">
        {dateMismatch ? (
          <span className="text-red-600">Departure date must be on or after arrival date.</span>
        ) : days > 0 ? (
          <span className="text-[#0f1b2d]"><span className="text-[#2563eb]">{days}</span> {days === 1 ? "day" : "days"} total</span>
        ) : (
          <span className="text-[#94a3b8]">Total days will be calculated automatically.</span>
        )}
      </div>

      <Field label="Number of entries requested" required>
        <SelectIn v={a.number_of_entries} on={(v) => set({ number_of_entries: v })} options={ENTRIES_OPTIONS} t={`${idp}-entries`} />
      </Field>
      <Field label="Did you have any Schengen visas issued during the past three years?" required>
        <YesNo v={a.previous_visa_3y} on={(v) => set({ previous_visa_3y: v })} t={`${idp}-prev-visa`} />
      </Field>
      {a.previous_visa_3y === "yes" && (
        <Row2>
          <Field label="Date of validity from" required><DateIn v={a.previous_visa_validity_from} on={(v) => set({ previous_visa_validity_from: v })} t={`${idp}-prev-from`} /></Field>
          <Field label="Date of validity until" required><DateIn v={a.previous_visa_validity_until} on={(v) => set({ previous_visa_validity_until: v })} t={`${idp}-prev-until`} /></Field>
        </Row2>
      )}
      <Field label="Travel insurance (acknowledgement)" required hint="I am aware of the need to have adequate travel medical insurance for my first and subsequent visit to the Schengen area.">
        <YesNo v={a.travel_insurance_ack} on={(v) => set({ travel_insurance_ack: v })} t={`${idp}-ins-ack`} />
      </Field>
      {showRelationship && (
        <Field label="Relationship to main applicant" required>
          <SelectIn v={a.relationship} on={(v) => set({ relationship: v })} options={RELATIONSHIP_OPTIONS} t={`${idp}-relationship`} />
        </Field>
      )}
    </Section>
  );
}

function AdditionalStep({ a, set, idp }) {
  const showCostOther = a.cost_covered_by === "Other";
  const means = Array.isArray(a.means_of_support) ? a.means_of_support : [];
  const showMeansOther = means.includes("Other");
  const toggleMean = (opt) => {
    const next = means.includes(opt) ? means.filter((m) => m !== opt) : [...means, opt];
    set({ means_of_support: next, ...(opt === "Other" && means.includes("Other") ? { means_of_support_other: "" } : {}) });
  };
  return (
    <>
      <Section title="Cost & Support">
        <Field label="By whom is the cost of travelling and living covered during the applicant's stay?" required>
          <SelectIn v={a.cost_covered_by} on={(v) => set({ cost_covered_by: v })} options={COST_COVERED_OPTIONS} t={`${idp}-cost-by`} />
        </Field>
        {showCostOther && (
          <Field label="Please specify who covers the cost" required>
            <TextIn v={a.cost_covered_by_other} on={(v) => set({ cost_covered_by_other: v })} t={`${idp}-cost-other`} />
          </Field>
        )}

        <Field label="Applicant's means of support" required hint="Select all that apply">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`${idp}-means-grid`}>
            {MEANS_OF_SUPPORT_OPTIONS.map((opt) => {
              const checked = means.includes(opt);
              const slug = opt.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <label key={opt} className={`flex items-center gap-2.5 px-3 py-2 rounded-md border cursor-pointer transition ${checked ? "border-[#2563eb] bg-[#eff6ff]" : "border-[#e2e8f0] bg-white hover:border-[#cbd5e1]"}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleMean(opt)}
                    data-testid={`${idp}-means-${slug}`}
                    className="accent-[#2563eb] w-4 h-4"
                  />
                  <span className="text-sm text-[#0f1b2d]">{opt}</span>
                </label>
              );
            })}
          </div>
          {means.length > 0 && (
            <div className="mt-2 text-xs text-[#64748b]" data-testid={`${idp}-means-summary`}>
              Selected: <span className="text-[#0f1b2d] font-medium">{means.join(", ")}</span>
            </div>
          )}
        </Field>
        {showMeansOther && (
          <Field label="Please specify other means of support" required>
            <TextIn v={a.means_of_support_other} on={(v) => set({ means_of_support_other: v })} t={`${idp}-means-other`} />
          </Field>
        )}
      </Section>

      <Section title="EU/EEA/CH/UK Family Member">
        <Field label="Do you have a family member who is an EU, EEA, CH citizen, or national of the UK?" required>
          <YesNo v={a.has_eu_family} on={(v) => set({ has_eu_family: v })} t={`${idp}-eu-family`} />
        </Field>
        {a.has_eu_family === "yes" && (
          <>
            <Row2>
              <Field label="Surname of family member" required><TextIn v={a.eu_family_surname} on={(v) => set({ eu_family_surname: v })} t={`${idp}-eu-surname`} /></Field>
              <Field label="First name of family member" required><TextIn v={a.eu_family_first_name} on={(v) => set({ eu_family_first_name: v })} t={`${idp}-eu-first`} /></Field>
            </Row2>
            <Row2>
              <Field label="Nationality of family member" required><CountrySelect v={a.eu_family_nationality} on={(v) => set({ eu_family_nationality: v })} t={`${idp}-eu-nat`} /></Field>
              <Field label="Date of birth of family member" required><DateIn v={a.eu_family_dob} on={(v) => set({ eu_family_dob: v })} t={`${idp}-eu-dob`} /></Field>
            </Row2>
            <Row2>
              <Field label="Passport number of family member" required><TextIn v={a.eu_family_passport} on={(v) => set({ eu_family_passport: v })} t={`${idp}-eu-pass`} /></Field>
              <Field label="Relationship" required><SelectIn v={a.eu_family_relationship} on={(v) => set({ eu_family_relationship: v })} options={EU_RELATIONSHIP_OPTIONS} t={`${idp}-eu-rel`} /></Field>
            </Row2>
            {a.eu_family_relationship === "Other" && (
              <Field label="EU citizen relationship — Other" required>
                <TextIn v={a.eu_family_relationship_other} on={(v) => set({ eu_family_relationship_other: v })} t={`${idp}-eu-rel-other`} />
              </Field>
            )}
          </>
        )}
      </Section>

      <Section title="Inviting Person / Company">
        <Field label="Costs are covered by the inviting person or company?" required>
          <YesNo v={a.inviting_costs} on={(v) => set({ inviting_costs: v })} t={`${idp}-inviting`} />
        </Field>
        {a.inviting_costs === "yes" && (
          <Field label="Costs are covered by" required>
            <TextIn v={a.inviting_covered_by} on={(v) => set({ inviting_covered_by: v })} t={`${idp}-inviting-by`} />
          </Field>
        )}
      </Section>
    </>
  );
}

/* ---- shared helpers ---- */
function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-5 sm:p-6 space-y-4">
      <div>
        <h3 className="font-display font-bold text-base text-[#0f1b2d]">{title}</h3>
        {subtitle && <p className="text-xs text-[#475569] mt-1">{subtitle}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row2({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, required, hint, hl, children }) {
  return (
    <label className={`block ${hl ? "ocr-hl" : ""}`}>
      <span className="text-xs font-semibold text-[#0f1b2d] flex items-center gap-1.5">
        {label} {required && <span className="text-red-500">*</span>}
        {hl && <span className="text-[10px] font-normal text-[#2563eb] bg-[#2563eb]/10 rounded px-1.5 py-0.5">from passport</span>}
        {hint && <span className="ml-2 text-[10px] text-[#64748b] font-normal">{hint}</span>}
      </span>
      <div className="mt-1.5">{children}</div>
      <style>{`
        .ocr-hl .inp,
        .ocr-hl input:not([type="hidden"]),
        .ocr-hl select,
        .ocr-hl button[role="combobox"] {
          background: #eff6ff !important;
          border-color: #93c5fd !important;
          box-shadow: 0 0 0 1px rgba(37,99,235,0.15) !important;
          transition: background-color 0.4s ease, border-color 0.4s ease, box-shadow 0.4s ease;
        }
      `}</style>
    </label>
  );
}
function TextIn({ v, on, t, type = "text", extra = "", optional, placeholder }) {
  return <input type={type} required={!optional} placeholder={placeholder} value={v || ""} onChange={(e) => on(e.target.value)} className={`inp ${extra}`} data-testid={t} />;
}
function PhoneIn({ v, on, t, optional }) {
  return <PhoneInput value={v} onChange={on} testId={t} optional={optional} />;
}
function DateIn({ v, on, t, optional }) {
  return <DateInput value={v} onChange={on} testId={t} optional={optional} />;
}
function SelectIn({ v, on, options, t, optional }) {
  return (
    <select required={!optional} value={v || ""} onChange={(e) => on(e.target.value)} className="inp" data-testid={t}>
      <option value="">Select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function YesNo({ v, on, t, optional, disabled }) {
  return (
    <select required={!optional} disabled={disabled} value={v || ""} onChange={(e) => on(e.target.value)} className="inp" data-testid={t}>
      <option value="">Select…</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
}
function CountrySelect({ v, on, t, optional }) {
  return (
    <select required={!optional} value={v || ""} onChange={(e) => on(e.target.value)} className="inp" data-testid={t}>
      <option value="">Select country…</option>
      {ALL_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}
function SchengenSelect({ v, on, t, optional }) {
  return (
    <CountryCombobox
      options={SCHENGEN_COUNTRIES}
      value={v || ""}
      onChange={(val) => on(val)}
      valueField="name"
      placeholder="Select Schengen country…"
      testId={t}
      required={!optional}
    />
  );
}
function MultiCountryChecks({ options, value, onChange, testIdPrefix }) {
  const toggle = (c) => {
    const next = value.includes(c) ? value.filter((x) => x !== c) : [...value, c];
    onChange(next);
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto p-2 border border-[#e2e8f0] rounded-md bg-white">
      {options.map((c) => (
        <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={value.includes(c)} onChange={() => toggle(c)} data-testid={`${testIdPrefix}-${c.toLowerCase().replace(/\s+/g, "-")}`} className="accent-[#2563eb]" />
          <span>{c}</span>
        </label>
      ))}
    </div>
  );
}

function NightsPerCountry({ a, set, idp, destinationCountry = "" }) {
  const rows = Array.isArray(a.destination_nights) ? a.destination_nights : [];
  const rowMode = (r) => r.mode || (r.nights !== "" && r.nights != null ? "custom" : "arrange");
  const total = rows.reduce((s, n) => s + (rowMode(n) === "custom" ? parseInt(n.nights, 10) || 0 : 0), 0);
  const stayDays = totalDays(a.intended_arrival_date, a.intended_departure_date);
  const allCustom = rows.length > 0 && rows.every((r) => rowMode(r) === "custom");
  const mismatch = allCustom && stayDays > 0 && total > 0 && total !== stayDays;

  // Wrong-consulate detection: find the row with the maximum nights and compare
  // against the destination row. If destination is missing from selected rows,
  // suppress the warning (no actionable signal).
  const destRow = rows.find((r) => r.country === destinationCountry);
  const destNights = parseInt(destRow?.nights, 10) || 0;
  let topRow = null;
  for (const r of rows) {
    const n = parseInt(r.nights, 10) || 0;
    if (!topRow || n > (parseInt(topRow.nights, 10) || 0)) topRow = r;
  }
  const topNights = parseInt(topRow?.nights, 10) || 0;
  const wrongConsulate =
    !!destinationCountry &&
    !!destRow &&
    destNights > 0 &&
    topNights > destNights &&
    topRow?.country !== destinationCountry;

  const updateRow = (i, nights) => {
    const next = rows.slice();
    let v = nights;
    if (v !== "") {
      const num = parseInt(v, 10);
      v = isNaN(num) ? "" : Math.max(1, Math.min(30, num));
    }
    next[i] = { ...rows[i], nights: v };
    set({ destination_nights: next });
  };

  const setRowMode = (i, mode) => {
    const next = rows.slice();
    next[i] = { ...rows[i], mode, ...(mode === "arrange" ? { nights: "" } : {}) };
    set({ destination_nights: next });
  };

  // Auto-balance: destination always gets the most nights. Returns updated rows.
  const distributeEvenly = () => {
    const n = rows.length;
    if (n === 0 || stayDays <= 0) return;
    const base = Math.floor(stayDays / n);
    const remainder = stayDays - base * n;
    const destIdx = destinationCountry ? rows.findIndex((r) => r.country === destinationCountry) : -1;

    let next;
    if (destIdx === -1) {
      // No destination row found — fall back to simple even distribution
      next = rows.map((r, i) => ({ ...r, mode: "custom", nights: base + (i < remainder ? 1 : 0) }));
    } else if (remainder === 0) {
      // Destination gets base+1, last non-destination gets base-1, others get base
      let lastNonDestIdx = -1;
      for (let i = n - 1; i >= 0; i--) {
        if (i !== destIdx) { lastNonDestIdx = i; break; }
      }
      next = rows.map((r, i) => {
        if (i === destIdx) return { ...r, mode: "custom", nights: base + 1 };
        if (i === lastNonDestIdx) return { ...r, mode: "custom", nights: Math.max(1, base - 1) };
        return { ...r, mode: "custom", nights: base };
      });
    } else {
      // Destination gets base+1, distribute remaining days evenly among others
      const destShare = base + 1;
      const remainingDays = stayDays - destShare;
      const numOthers = n - 1;
      const otherBase = Math.floor(remainingDays / numOthers);
      const otherRem = remainingDays - otherBase * numOthers;
      let otherIdx = 0;
      next = rows.map((r, i) => {
        if (i === destIdx) return { ...r, mode: "custom", nights: destShare };
        const v = otherBase + (otherIdx < otherRem ? 1 : 0);
        otherIdx += 1;
        return { ...r, mode: "custom", nights: v };
      });
    }
    set({ destination_nights: next });
  };

  return (
    <div className="mt-3 rounded-md border border-[#e2e8f0] bg-white px-3 py-3" data-testid={`${idp}-nights-section`}>
      <div className="text-sm font-semibold text-[#0f1b2d] mb-2">How many nights will you spend in each country?</div>

      {/* 1. Schengen rule info banner — always visible with 2+ countries */}
      {destinationCountry && (
        <div
          data-testid={`${idp}-schengen-rule-info`}
          className="mb-3 flex items-start gap-2.5 rounded-md bg-[#eff6ff] border-l-2 border-[#2563eb] px-3 py-2.5 text-[12px] leading-relaxed text-[#1e3a8a]"
        >
          <Info className="w-4 h-4 text-[#2563eb] flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p>
              <span className="font-semibold">Schengen visa rule:</span> Your visa must be issued by the consulate of the country where you will spend the most nights. If you are applying at the <span className="font-semibold">{destinationCountry}</span> consulate, make sure <span className="font-semibold">{destinationCountry}</span> has the highest number of nights in your itinerary.
            </p>
            <p className="mt-1.5">
              If another country has more nights, you may need to apply at that country&apos;s consulate instead.
            </p>
          </div>
        </div>
      )}

      {/* 2. Per-country rows: "We'll arrange" (default) or custom nights */}
      <div className="space-y-2">
        {rows.map((n, i) => {
          const sc = SCHENGEN_COUNTRIES.find((c) => c.name === n.country);
          const key = n.code || sc?.code || n.country;
          const mode = rowMode(n);
          return (
            <div key={key} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`${idp}-nights-row-${key}`}>
              <span className="text-sm text-[#0f1b2d] inline-flex items-center gap-1.5">
                {sc?.code && <Flag code={sc.code} />}
                {n.country}
              </span>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-[#475569] cursor-pointer whitespace-nowrap">
                  <input
                    type="radio"
                    name={`${idp}-nights-mode-${key}`}
                    checked={mode === "arrange"}
                    onChange={() => setRowMode(i, "arrange")}
                    data-testid={`${idp}-nights-arrange-${key}`}
                    className="accent-[#2563eb]"
                  />
                  We&apos;ll arrange
                </label>
                <label className="flex items-center gap-1.5 text-xs text-[#475569] cursor-pointer whitespace-nowrap">
                  <input
                    type="radio"
                    name={`${idp}-nights-mode-${key}`}
                    checked={mode === "custom"}
                    onChange={() => setRowMode(i, "custom")}
                    data-testid={`${idp}-nights-mode-custom-${key}`}
                    className="accent-[#2563eb]"
                  />
                  {mode === "custom" ? (
                    <>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        inputMode="numeric"
                        value={n.nights === "" || n.nights == null ? "" : n.nights}
                        onChange={(e) => updateRow(i, e.target.value)}
                        data-testid={`${idp}-nights-input-${key}`}
                        className="w-16 border border-[#e2e8f0] bg-white text-[#0f1b2d] rounded-md px-2 py-1 text-sm outline-none focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/15"
                      />
                      nights
                    </>
                  ) : (
                    "Set nights"
                  )}
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {/* 3. Running total */}
      <div className="mt-3 pt-2 border-t border-[#e2e8f0] flex items-center justify-between text-sm font-semibold" data-testid={`${idp}-nights-total`}>
        <span className="text-[#0f1b2d]">Total:</span>
        <span className="text-[#2563eb]">{total} {total === 1 ? "night" : "nights"}</span>
      </div>

      {/* 4. Mismatch warning (only when every country has custom nights) */}
      {mismatch && (
        <div className="mt-2 px-3 py-2 rounded-md bg-[#fff7ed] border border-[#fed7aa] text-xs text-[#9a3412]" data-testid={`${idp}-nights-warning`}>
          ⚠️ Total nights per country ({total}) doesn&apos;t match your stay duration ({stayDays} days). Please check.
        </div>
      )}

      {/* 5. Distribute-evenly helper */}
      {stayDays > 0 && rows.length > 0 && (
        <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-2 rounded-md bg-[#eff6ff] border border-[#bfdbfe]" data-testid={`${idp}-nights-suggestion`}>
          <span className="text-xs text-[#0f1b2d]">💡 Want us to distribute the nights evenly?</span>
          <button
            type="button"
            onClick={distributeEvenly}
            data-testid={`${idp}-nights-distribute-evenly`}
            className="inline-flex items-center justify-center bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-xs font-semibold px-3 py-1.5 rounded-md transition whitespace-nowrap"
          >
            Yes, distribute evenly
          </button>
        </div>
      )}

      {/* 6. Wrong-consulate warning (after the mismatch block) */}
      {wrongConsulate && (
        <div
          data-testid={`${idp}-nights-wrong-consulate`}
          className="mt-2 flex items-start gap-2.5 rounded-md bg-[#fff7ed] border-l-2 border-[#f97316] px-3 py-2.5 text-xs text-[#9a3412]"
        >
          <AlertTriangle className="w-4 h-4 text-[#f97316] flex-shrink-0 mt-0.5" />
          <div className="leading-relaxed min-w-0">
            <p>
              You are applying at the <span className="font-semibold">{destinationCountry}</span> consulate, but your itinerary shows more nights in <span className="font-semibold">{topRow?.country}</span> ({topNights} nights vs {destNights} nights).
            </p>
            <p className="mt-1">
              This may cause your visa application to be rejected. Consider applying at the <span className="font-semibold">{topRow?.country}</span> consulate, or adjust your nights distribution.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
